import { ref, computed } from 'vue';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import { PLAN_LIMITS_LOGIC } from '@/services/constants/planLimits';

export type PlanLimitsGrouped = {
  logic: PLAN_LIMITS_LOGIC.AT_LEAST_ONE_REQUIRED | PLAN_LIMITS_LOGIC.ALL_REQUIRED;
  addons: (keyof BillingLimits)[];
};

export type PlanLimitsProp = keyof BillingLimits | PlanLimitsGrouped;

export type PlanLimitsKeyLabelsShort = Partial<Record<keyof BillingLimits, string>>;

export type ReportColumnPlanLimitsItem = { value: string };

export const getReportColumnPlanLimits = (
  item: ReportColumnPlanLimitsItem,
): PlanLimitsProp | undefined => {
  switch (item.value) {
    case 'reports-clickssuspicious':
    case 'reports-visitssuspicious':
    case 'reports-impressionssuspicious':
      return {
        logic: PLAN_LIMITS_LOGIC.AT_LEAST_ONE_REQUIRED,
        addons: [
          'ivtIpIsDatacenter',
          'ivtIpIsBlacklisted',
          'ivtFastClicker',
          'ivtIpWithFrequentCampaignEvents',
          'ivtIpWithFrequentEvents',
        ],
      };
    case 'reports-impressionssuspiciousdatacenterips':
    case 'reports-visitssuspiciousdatacenterips':
    case 'reports-clickssuspiciousdatacenterips':
    case 'reports-datacenterip':
      return 'ivtIpIsDatacenter';
    case 'reports-visitssuspiciousblacklistedips':
    case 'reports-clickssuspiciousblacklistedips':
    case 'reports-impressionssuspiciousblacklistedips':
    case 'reports-blacklistedip':
      return 'ivtIpIsBlacklisted';
    case 'reports-impressions_suspicious_ip_with_frequent_events':
    case 'reports-visits_suspicious_ip_with_frequent_events':
    case 'reports-clicks_suspicious_ip_with_frequent_events':
      return 'ivtIpWithFrequentEvents';
    case 'reports-impressions_suspicious_ip_with_frequent_campaign_events':
    case 'reports-visits_suspicious_ip_with_frequent_campaign_events':
      return 'ivtIpWithFrequentCampaignEvents';
    case 'reports-clicks_fast_clicker':
      return 'ivtFastClicker';
    case 'reports-clicks_suspicious_ip_with_frequent_campaign_events':
      return 'ivtIpWithFrequentCampaignEvents';
    case 'live_clicks-fast_clicker':
      return 'ivtFastClicker';
    case 'live_visits-ip_with_frequent_events':
    case 'live_clicks-ip_with_frequent_events':
      return 'ivtIpWithFrequentEvents';
    case 'live_visits-ip_with_frequent_campaign_events':
    case 'live_clicks-ip_with_frequent_campaign_events':
      return 'ivtIpWithFrequentCampaignEvents';
    default:
      return undefined;
  }
};

export function usePlanLimits(
  planLimits: PlanLimitsProp | (() => PlanLimitsProp | undefined) | undefined,
  planLimitsEditable: boolean | (() => boolean) = false,
) {
  const store = useStore();
  const { t, locale } = useI18n();

  const isChangePlanDialogLoading = ref(false);

  const planLimitsKeysGrouping = computed(() => {
    void locale.value;
    return [
      { name: `${t('Data retention')} (${t('months')})`, keys: ['dataRetention'] },
      {
        name: t('Amount of Active Elements'),
        keys: [
          'campaignsAmount',
          'offersAmount',
          'landingsAmount',
          'flowsAmount',
          'trafficSourcesAmount',
          'affiliateNetworksAmount',
        ],
      },
      { name: t('Domains'), keys: ['domainsAmount', 'redirectDomainsAmount'] },
      {
        name: t('Traffic Distribution'),
        keys: [
          'webViewCondition',
          'crawlerCondition',
          'proxyCondition',
          'referrerDomainCondition',
          'botMarkCondition',
          'uniquenessPeriodCondition',
          'visitCapCondition',
          'uniqOfferViewerCondition',
        ],
      },
      {
        name: t('Bulk Edit'),
        keys: ['bulkEditCampaigns', 'bulkEditOffers', 'bulkEditLandings'],
      },
      { name: t('Import Settings'), keys: ['importOffers', 'importLandings'] },
      {
        name: t('Bulk Reports'),
        keys: [
          'bulkReportCampaign',
          'bulkReportOffer',
          'bulkReportLanding',
          'bulkReportFlow',
          'bulkReportAffiliateNetwork',
          'bulkReportTrafficSource',
          'bulkReportCustomParams',
          'bulkReportBrowserNames',
          'bulkReportBrowserVersions',
          'bulkReportCities',
          'bulkReportConnectionTypes',
          'bulkReportCountries',
          'bulkReportDay',
          'bulkReportDayOfWeek',
          'bulkReportDeviceModels',
          'bulkReportDeviceTypes',
          'bulkReportDeviceVendors',
          'bulkReportHourOfDay',
          'bulkReportIp',
          'bulkReportIsp',
          'bulkReportLanguages',
          'bulkReportMobileCarriers',
          'bulkReportMonth',
          'bulkReportOs',
          'bulkReportOsVersions',
          'bulkReportReferrerDomains',
          'bulkReportReferrers',
          'bulkReportScreenResolutions',
          'bulkReportStatesRegions',
          'bulkReportSubnetBig',
          'bulkReportSubnetSmall',
          'bulkReportUserAgents',
        ],
      },
      {
        name: t('Marks'),
        keys: [
          'markersCampaigns',
          'markersOffers',
          'markersLandings',
          'markersFlows',
          'markersAffiliateNetworks',
          'markersTrafficSources',
          'markersCustomParams',
        ],
      },
      {
        name: t('Team Work'),
        keys: ['memberAdminsAmount', 'memberUsersAmount', 'sharedReportsAmount'],
      },
      {
        name: t('Tags'),
        keys: [
          'tagsCampaigns',
          'tagsOffers',
          'tagsLandings',
          'tagsFlows',
          'tagsAffiliateNetworks',
          'tagsTrafficSources',
        ],
      },
      {
        name: t('Integrations'),
        keys: [
          'integrationMgid',
          'integrationExoclick',
          'integrationPropellerads',
          'integrationRevcontent',
          'integrationFacebookConversions',
          'integrationTrafficjunky',
        ],
      },
      {
        name: t('Advanced Options'),
        keys: [
          'sendPostbacksCampaign',
          'trafficLossCampaign',
          'customColumnsAmount',
          'customConversionsAmount',
          'botFiltersAccess',
          'doubleMetaRedirect',
          'uniquenessPeriod',
          'landingPageProtection',
          'trafficSourceParamMasking',
          'smartRotations',
          'presetAmount',
          'compareReport',
        ],
      },
      {
        name: t('Invalid Traffic Tracking'),
        nameHelp: t(
          'Activate the metrics to track the suspicious traffic. <br> The data on each metric will be displayed in the separate column of suspicious visits, clicks, impressions in your reports.',
        ),
        keys: [
          'ivtIpIsDatacenter',
          'ivtIpIsBlacklisted',
          'ivtFastClicker',
          'ivtIpWithFrequentCampaignEvents',
          'ivtIpWithFrequentEvents',
        ],
      },
    ];
  });

  const planLimitsValue = computed(() =>
    typeof planLimits === 'function' ? planLimits() : planLimits,
  );
  const planLimitsEditableValue = computed(() =>
    typeof planLimitsEditable === 'function' ? planLimitsEditable() : planLimitsEditable,
  );

  const $PL = computed(() => store.billing.limits ?? {});

  function hasAddonRestriction(planLimit: keyof BillingLimits) {
    if (typeof $PL.value[planLimit] === 'number') {
      const { limits, usage } = store.billing;
      return +limits[planLimit] <= +usage[planLimit];
    }
    return $PL.value[planLimit] === false;
  }

  const hasPlanLimitsRestriction = (limits: PlanLimitsProp | undefined) => {
    if (limits == null) return false;
    if (limits && typeof limits === 'object' && 'logic' in limits && 'addons' in limits) {
      const addons = limits.addons.map(planLimit => hasAddonRestriction(planLimit));
      if (limits.logic === PLAN_LIMITS_LOGIC.ALL_REQUIRED) {
        return addons.some(item => item === true);
      }
      if (limits.logic === PLAN_LIMITS_LOGIC.AT_LEAST_ONE_REQUIRED) {
        return !addons.some(item => item === false);
      }
    }
    return hasAddonRestriction(limits as keyof BillingLimits);
  };

  const showPlanLimitsIcon = computed(() =>
    hasPlanLimitsRestriction(planLimitsValue.value),
  );

  function handleClickPlanLimits(limits = planLimitsValue.value) {
    if (
      hasPlanLimitsRestriction(limits) &&
      !isChangePlanDialogLoading.value &&
      !planLimitsEditableValue.value
    ) {
      if (store.user?.isMember) {
        return $emitter.emit(
          'toast-error',
          t(
            'The option is not available on your plan. Contact the account owner to upgrade and get the access.',
          ),
        );
      }
      isChangePlanDialogLoading.value = true;
      return $emitter.emit('open-dialog', {
        name: 'ChangePlanDialog',
        data: {
          planLimits: limits,
          actionAfterLoad: () => {
            isChangePlanDialogLoading.value = false;
          },
        },
      });
    }
  }

  const planLimitsKeyText = computed((): PlanLimitsKeyLabelsShort => {
    void locale.value;
    return {
      eventsAmount: t('Events'),
      dataRetention: t('Data retention'),
      domainsAmount: t('Tracking domains'),
      campaignsAmount: t('Campaigns'),
      offersAmount: t('Offers'),
      landingsAmount: t('Landing pages'),
      flowsAmount: t('Flows'),
      trafficSourcesAmount: t('Traffic sources'),
      affiliateNetworksAmount: t('Affiliate networks'),
      customColumnsAmount: t('Custom columns'),
      memberAdminsAmount: t('Admins'),
      memberUsersAmount: t('Users'),
      redirectDomainsAmount: t('Cloaking domains'),
      customConversionsAmount: t('Custom conversions'),
      sharedReportsAmount: t('Shared reports'),
      bulkEditCampaigns: t('Access to campaigns bulk edit'),
      bulkEditOffers: t('Access to offers bulk edit'),
      bulkEditLandings: t('Access to landings bulk edit'),
      webViewCondition: t('Webview condition for traffic distribution'),
      crawlerCondition: t('Crawler condition for traffic distribution'),
      proxyCondition: t('Proxy condition for traffic distribution'),
      referrerDomainCondition: t('Referrer domain condition for traffic distribution'),
      botMarkCondition: t('Bot mark condition for traffic distribution'),
      uniquenessPeriodCondition: t('Uniqueness condition for traffic distribution'),
      visitCapCondition: t('Visit Cap condition for traffic distribution'),
      doubleMetaRedirect: t('Double meta redirect'),
      uniquenessPeriod: t('Uniqueness period in campaign settings'),
      bulkReportCampaign: t('Bulk reports on campaigns'),
      bulkReportOffer: t('Bulk reports on offers'),
      bulkReportLanding: t('Bulk reports on landings'),
      bulkReportFlow: t('Bulk reports on flows'),
      bulkReportTrafficSource: t('Bulk reports on traffic sources'),
      bulkReportAffiliateNetwork: t('Bulk reports on affiliate networks'),
      bulkReportCustomParams: t('Bulk reports on Custom Params'),
      importOffers: t('Import offers from the file'),
      importLandings: t('Import landings from the file'),
      markersCampaigns: t('Marks for campaigns'),
      markersOffers: t('Marks for offers'),
      markersLandings: t('Marks for landings'),
      markersFlows: t('Marks for flows'),
      markersAffiliateNetworks: t('Marks for affiliate networks'),
      markersTrafficSources: t('Marks for traffic sources'),
      markersCustomParams: t('Marks for custom params'),
      sendPostbacksCampaign: t('Share Conversions %'),
      trafficLossCampaign: t('Traffic loss % in campaigns'),
      landingPageProtection: t('Landing page protection'),
      tagsCampaigns: t('Tags for campaigns'),
      tagsOffers: t('Tags for offers'),
      tagsLandings: t('Tags for landings'),
      tagsFlows: t('Tags for flows'),
      tagsTrafficSources: t('Tags for traffic sources'),
      tagsAffiliateNetworks: t('Tags for affiliate networks'),
      trafficSourceParamMasking: t('Ghost ID (traffic source params masking)'),
      botFiltersAccess: t('Access to bot filters'),
      integrationMgid: t('Integration with MGID'),
      integrationExoclick: t('Integration with Exoclick'),
      integrationTrafficjunky: t('Integration with TrafficJunky'),
      integrationPropellerads: t('Integration with PropellerAds'),
      integrationFacebookConversions: t('Integration with Facebook'),
      integrationRevcontent: t('Integration with Revcontent'),
      smartRotations: t('Smart Rotations'),
      presetAmount: t('Presets'),
      ivtIpIsDatacenter: t('IPs from data centers'),
      ivtIpIsBlacklisted: t('IPs from black lists'),
      ivtFastClicker: t('Fast Clicks'),
      ivtIpWithFrequentEvents: t('Frequent Events'),
      ivtIpWithFrequentCampaignEvents: t('Frequent Campaign Events'),
      bulkReportBrowserNames: t('Bulk reports on Browsers Names'),
      bulkReportBrowserVersions: t('Bulk reports on Browsers Versions'),
      bulkReportCities: t('Bulk reports on Cities'),
      bulkReportConnectionTypes: t('Bulk reports on Connection Types'),
      bulkReportCountries: t('Bulk reports on Countries'),
      bulkReportDay: t('Bulk reports on Day'),
      bulkReportDayOfWeek: t('Bulk reports on Day Of Week'),
      bulkReportDeviceModels: t('Bulk reports on Device Models'),
      bulkReportDeviceTypes: t('Bulk reports on Device Types'),
      bulkReportDeviceVendors: t('Bulk reports on Device Vendors'),
      bulkReportHourOfDay: t('Bulk reports on Hour of Day'),
      bulkReportIp: t('Bulk reports on IPs'),
      bulkReportIsp: t('Bulk reports on ISP'),
      bulkReportLanguages: t('Bulk reports on Languages'),
      bulkReportMobileCarriers: t('Bulk reports on Mobile Carriers'),
      bulkReportMonth: t('Bulk reports on Month'),
      bulkReportOs: t('Bulk reports on OS'),
      bulkReportOsVersions: t('Bulk reports on OS Versions'),
      bulkReportReferrerDomains: t('Bulk reports on Referrer Domains'),
      bulkReportReferrers: t('Bulk reports on Referrers'),
      bulkReportScreenResolutions: t('Bulk reports on Screen Resolutions'),
      bulkReportStatesRegions: t('Bulk reports on State/ Region'),
      bulkReportSubnetBig: t('Bulk reports on Subnet Big'),
      bulkReportSubnetSmall: t('Bulk reports on Subnet Small'),
      bulkReportUserAgents: t('Bulk reports on User Agent'),
      uniqOfferViewerCondition: t('Recurring Conversion Condition'),
      compareReport: t('Compare Reports'),
    };
  });

  const planLimitsKeyTextShort = computed((): PlanLimitsKeyLabelsShort => {
    void locale.value;
    return {
      ...planLimitsKeyText.value,
      webViewCondition: t('Webview Condition'),
      uniqOfferViewerCondition: t('Recurring Conversion Condition'),
      crawlerCondition: t('Crawler Condition'),
      proxyCondition: t('Proxy Condition'),
      referrerDomainCondition: t('Referrer Domain Condition'),
      botMarkCondition: t('Bot Mark Condition'),
      uniquenessPeriodCondition: t('Uniqueness Period Condition'),
      visitCapCondition: t('Visit Cap Condition'),
      bulkEditCampaigns: t('Campaigns'),
      bulkEditOffers: t('Offers'),
      bulkEditLandings: t('Landings'),
      importOffers: t('Offers'),
      importLandings: t('Landings'),
      bulkReportCampaign: t('Campaigns'),
      bulkReportOffer: t('Offers'),
      bulkReportLanding: t('Landings'),
      bulkReportFlow: t('Flows'),
      bulkReportCustomParams: t('Custom Params'),
      bulkReportAffiliateNetwork: t('Affiliate Networks'),
      bulkReportTrafficSource: t('Traffic Sources'),
      markersCampaigns: t('Campaigns'),
      markersOffers: t('Offers'),
      markersLandings: t('Landings'),
      markersFlows: t('Flows'),
      markersAffiliateNetworks: t('Affiliate Networks'),
      markersTrafficSources: t('Affiliate Networks'),
      markersCustomParams: t('Custom Params'),
      memberAdminsAmount: t('Admins'),
      memberUsersAmount: t('Users'),
      sharedReportsAmount: t('Shared Reports'),
      tagsCampaigns: t('Campaigns'),
      tagsOffers: t('Tags for offers'),
      tagsLandings: t('Tags for landings'),
      tagsFlows: t('Tags for flows'),
      tagsAffiliateNetworks: t('Tags for affiliate networks'),
      tagsTrafficSources: t('Tags for traffic sources'),
      sendPostbacksCampaign: t('Send Postbacks %'),
      botFiltersAccess: t('Bot Rules Access'),
      uniquenessPeriod: t('Uniqueness Period'),
      trafficSourceParamMasking: t('Ghost ID (traffic source params masking)'),
      bulkReportBrowserNames: t('Browsers Names'),
      bulkReportBrowserVersions: t('Browsers Versions'),
      bulkReportCities: t('Cities'),
      bulkReportConnectionTypes: t('Connection Types'),
      bulkReportCountries: t('Countries'),
      bulkReportDay: t('Day'),
      bulkReportDayOfWeek: t('Day Of Week'),
      bulkReportDeviceModels: t('Device Models'),
      bulkReportDeviceTypes: t('Device Types'),
      bulkReportDeviceVendors: t('Device Vendors'),
      bulkReportHourOfDay: t('Hour of Day'),
      bulkReportIp: t('IPs'),
      bulkReportIsp: t('ISP'),
      bulkReportLanguages: t('Languages'),
      bulkReportMobileCarriers: t('Mobile Carriers'),
      bulkReportMonth: t('Month'),
      bulkReportOs: t('OS'),
      bulkReportOsVersions: t('OS Versions'),
      bulkReportReferrerDomains: t('Referrer Domains'),
      bulkReportReferrers: t('Referrers'),
      bulkReportScreenResolutions: t('Screen Resolutions'),
      bulkReportStatesRegions: t('State/ Region'),
      bulkReportSubnetBig: t('Subnet Big'),
      bulkReportSubnetSmall: t('Subnet Small'),
      bulkReportUserAgents: t('User Agent'),
      integrationMgid: 'MGID',
      integrationExoclick: 'Exoclick',
      integrationTrafficjunky: 'TrafficJunky',
      integrationPropellerads: 'PropellerAds',
      integrationFacebookConversions: 'Facebook',
      integrationRevcontent: 'Revcontent',
    };
  });

  function conditionPlanLimits(
    condition: string,
  ): keyof BillingLimits | undefined {
    const map: Partial<Record<string, keyof BillingLimits>> = {
      webViewMark: 'webViewCondition',
      uniqOfferViewer: 'uniqOfferViewerCondition',
      externalId: 'externalIdCondition',
      crawler: 'crawlerCondition',
      proxy: 'proxyCondition',
      visitCap: 'visitCapCondition',
      botMark: 'botMarkCondition',
      referrerDomain: 'referrerDomainCondition',
      uniqueness: 'uniquenessPeriodCondition',
      ipDataCenter: 'ivtIpIsDatacenter',
      ipBlacklisted: 'ivtIpIsBlacklisted',
      ipWithFrequentEvents: 'ivtIpWithFrequentEvents',
      ipWithFrequentCampaignEvents: 'ivtIpWithFrequentCampaignEvents',
    };
    return map[condition];
  }

  function integrationsPlanLimits(condition: string) {
    const map: Record<string, string> = {
      propeller: 'integrationPropellerads',
      revcontent: 'integrationRevcontent',
      mgid: 'integrationMgid',
      exoclick: 'integrationExoclick',
      trafficjunky: 'integrationTrafficjunky',
      'facebook-conversions': 'integrationFacebookConversions',
    };
    return map[condition];
  }

  return {
    isChangePlanDialogLoading,
    planLimitsKeysGrouping,
    showPlanLimitsIcon,
    planLimitsKeyText,
    planLimitsKeyTextShort,
    hasAddonRestriction,
    hasPlanLimitsRestriction,
    handleClickPlanLimits,
    conditionPlanLimits,
    integrationsPlanLimits,
    $PL,
  };
}
